/**
 * ExpensesLauncher — file an expense, approve one, record one.
 *
 * Opens via CustomEvent('open-expenses-panel') from SiteNav. Mounted once in OutlineView.
 *
 * Three tabs, which are the three jobs and the three permissions:
 *   Mine        everyone — file a new expense, fix a rejected one, watch it move
 *   To approve  managers + admin (`timecard-admin`)
 *   To record   accounting + Matt (`finance-admin`)
 *
 * A tab is only shown when the server says the caller may use it — and the server decides again
 * on every call, so hiding a tab is a courtesy, never the control.
 *
 * Flat data, so a centered directory popup rather than an outline: the same reasoning as Contacts
 * (CLAUDE.md, "Modules: outline for hierarchy, directory popup for flat reference data").
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  listExpenseReports, saveExpenseReport, decideExpenseReport,
  markExpenseRecorded, uploadReceipt, expenseItemCodes, isMileage,
  retryExpenseCore, listOwed, markExpensesPaid,
} from '../services/expenseReports.js'
import { getActiveProjects } from '../services/commands.js'
import { getEntryPhases } from '../services/timecards.js'
import { promptDialog, confirmDialog, alertDialog } from './Dialog.jsx'
import { useDarkMode } from '../outline/theme.js'
import Select from './Select.jsx'

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * What to show in the amount column.
 *
 * A mileage claim with no rate is worth an unknown amount, not zero. Printing $0.00 would make a
 * debt to a colleague look like nothing — 65 real site-visit claims are in exactly that state.
 */
function AmountCell({ r, dark }) {
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.50)'
  if (r.amount == null) {
    return (
      <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
        <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: textPrim }}>
          {Number(r.units || 0).toLocaleString()} mi
        </div>
        <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 9, color: '#d97706' }} title="No rate for this item in Core, so the claim has no dollar value yet.">
          no rate in Core
        </div>
      </div>
    )
  }
  // For a priced mileage claim, show the working. The rate comes from Core's expense item, so
  // saying so is the difference between a number to trust and a number to query.
  const isRated = Number(r.units) > 0 && Number(r.rate) > 0 && String(r.itemCode || '').includes('Mileage')
  return (
    <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
      <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 12, color: textPrim }}>{money(r.amount)}</div>
      {isRated && (
        <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 9, color: textMut }} title="Rate from the expense item in BQE Core">
          {Number(r.units).toLocaleString()} mi @ ${r.rate}
        </div>
      )}
    </div>
  )
}
const shortDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '')

const STATUS_COLOR = {
  'Submitted': '#2563eb',
  'Approved': '#1D9E75',
  'Rejected': '#c0392b',
  'Recorded in BQE Core': '#7a9aaa',
}

function StatusPill({ status, dark }) {
  const c = STATUS_COLOR[status] || '#7a9aaa'
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 20,
      fontFamily: '"DM Mono", monospace', fontSize: 10, whiteSpace: 'nowrap',
      color: c, border: `0.5px solid ${c}`,
      background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
    }}>
      {status === 'Recorded in BQE Core' ? 'In Core' : status}
    </span>
  )
}

/* ── The form ─────────────────────────────────────────────────────────────
   One expense at a time. Deliberately not a multi-line "expense report" with a header and rows:
   the underlying table is one row per expense, every existing record is shaped that way, and
   inventing a header entity here would need a migration before anyone could file anything.     */
function ExpenseForm({ initial, projects, dark, onSaved, onCancel }) {
  const [f, setF] = useState(() => ({
    id: initial?.id || null,
    date: initial?.date || new Date().toISOString().slice(0, 10),
    payee: initial?.payee || '',
    payor: initial?.payor || 'Employee',
    expenseType: initial?.expenseType || 'Project',
    itemCode: initial?.itemCode || '',
    projectId: initial?.projectId || null,
    phaseId: initial?.phaseId || '',
    memo: initial?.memo || '',
    unitCost: initial?.unitCost ?? '',
    units: initial?.units ?? '',
  }))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const fileRef = useRef(null)
  const [phases, setPhases] = useState([])
  useEffect(() => { getEntryPhases().then(l => setPhases((l || []).map(p => ({ id: p.id, name: p.name })))).catch(() => setPhases([])) }, [])

  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  const mileage = isMileage(f.itemCode)
  // The phase decides which Core project and invoice it lands on (required, Matt 2026-10-28):
  // the phases open for time on the chosen project, or the overhead project's.
  const proj = projects.find(p => p.id === f.projectId)
  const phaseOptions = f.expenseType === 'Overhead'
    ? phases.filter(p => /overhead/i.test(p.name))
    : (proj && proj.number ? phases.filter(p => String(p.name).startsWith(proj.number + '-') || String(p.name).startsWith(proj.number + ' ')) : [])
  const phaseLabel = (n) => { const parts = String(n).split(' - '); return parts.length > 2 ? parts.slice(2).join(' - ') : n }

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.50)'
  const inputStyle = {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textPrim,
    background: dark ? 'rgba(255,255,255,0.05)' : '#f4f7f8',
    border: `0.5px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.15)'}`,
    borderRadius: 4, padding: '6px 9px', width: '100%', outline: 'none', boxSizing: 'border-box',
  }
  const label = { fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMut, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4, display: 'block' }

  async function save() {
    setBusy(true); setErr('')
    try {
      const res = await saveExpenseReport(f)
      // Attach the receipt only after the row exists — there is nothing to attach it to before.
      const file = fileRef.current?.files?.[0]
      if (file) {
        try { await uploadReceipt(res.report.id, file) }
        catch { await alertDialog({ title: 'Saved, but the receipt did not upload', message: 'The expense was filed. Open it again to add the receipt.' }) }
      }
      onSaved()
    } catch (e) {
      setErr(e.message || 'Could not save.')
    } finally { setBusy(false) }
  }

  return (
    <div style={{ padding: '14px 20px 18px', borderBottom: `0.5px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.08)'}` }}>
      {initial && initial.status === 'Rejected' && initial.workflowMemo && (
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b', background: 'rgba(192,57,43,0.07)', border: '0.5px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
          <b>Sent back:</b> {String(initial.workflowMemo).split(' — ')[0]}. Fix it below and save to send it again.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={label}>Date</label>
          <input type="date" value={f.date || ''} onChange={e => set('date', e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={label}>Paid to</label>
          <input value={f.payee} onChange={e => set('payee', e.target.value)} placeholder="City of Laguna Beach" style={inputStyle} />
        </div>
        <div>
          <label style={label}>Who paid</label>
          <Select
            value={f.payor}
            onChange={v => set('payor', v)}
            options={[
              { value: 'Employee', label: 'I did — reimburse me' },
              { value: 'Alder James', label: 'Alder James paid' },
            ]}
            dark={dark}
            style={{ fontSize: 13, padding: '6px 9px', width: '100%' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={label}>Expense type</label>
          <Select
            value={f.itemCode}
            onChange={v => set('itemCode', v)}
            options={expenseItemCodes().map(c => ({ value: c, label: c.replace(/^EX-\s*/, '') }))}
            placeholder="— choose —"
            dark={dark}
            style={{ fontSize: 13, padding: '6px 9px', width: '100%' }}
          />
        </div>
        <div>
          <label style={label}>{mileage ? 'Rate per mile' : 'Amount'}</label>
          <input
            type="number" step="0.01" min="0" value={f.unitCost}
            onChange={e => set('unitCost', e.target.value)}
            placeholder="0.00" style={{ ...inputStyle, fontFamily: '"DM Mono", monospace' }}
          />
        </div>
        <div>
          <label style={label}>{mileage ? 'Miles' : 'Units (optional)'}</label>
          <input
            type="number" step="1" min="0" value={f.units}
            onChange={e => set('units', e.target.value)}
            placeholder={mileage ? 'miles driven' : '—'}
            style={{ ...inputStyle, fontFamily: '"DM Mono", monospace' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '150px 2fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={label}>Charge to</label>
          <Select
            value={f.expenseType}
            onChange={v => set('expenseType', v)}
            options={[{ value: 'Project', label: 'A project' }, { value: 'Overhead', label: 'Overhead' }]}
            dark={dark}
            style={{ fontSize: 13, padding: '6px 9px', width: '100%' }}
          />
        </div>
        <div>
          <label style={label}>Project</label>
          <Select
            value={f.projectId || ''}
            onChange={v => set('projectId', v || null)}
            options={projects.map(p => ({ value: p.id, label: `${p.number || ''} ${p.name || ''}`.trim() }))}
            placeholder={f.expenseType === 'Overhead' ? 'not needed for overhead' : '— choose a project —'}
            dark={dark}
            style={{ fontSize: 13, padding: '6px 9px', width: '100%' }}
          />
        </div>
        <div>
          <label style={label}>Receipt</label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf"
            style={{ ...inputStyle, padding: '4px 6px', fontSize: 11 }} />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={label}>Phase</label>
        <Select
          value={phaseOptions.some(p => p.id === f.phaseId) ? f.phaseId : ''}
          onChange={v => set('phaseId', v || '')}
          options={phaseOptions.map(p => ({ value: p.id, label: phaseLabel(p.name) }))}
          placeholder={f.expenseType !== 'Overhead' && !f.projectId ? 'Pick the project first' : (phaseOptions.length ? '— choose the phase —' : 'No phase open for time on this project')}
          dark={dark}
          style={{ fontSize: 13, padding: '6px 9px', width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={label}>What it was for</label>
        <textarea
          value={f.memo} onChange={e => set('memo', e.target.value)} rows={2}
          placeholder="Plan check fee, Carstairs Residence — paid at the counter"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      {err && <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#c0392b', marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={save} disabled={busy}
          style={{
            fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500,
            color: '#fff', background: '#07272D', border: 'none', borderRadius: 4,
            padding: '7px 16px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Saving…' : (f.id ? 'Resubmit' : 'Submit expense')}
        </button>
        <button
          onClick={onCancel}
          style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Cancel
        </button>
        {mileage && (
          <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: textMut, marginLeft: 'auto' }}>
            {money((Number(f.units) || 0) * (Number(f.unitCost) || 0))}
          </span>
        )}
      </div>
    </div>
  )
}

/* ── One row ──────────────────────────────────────────────────────────── */
function Row({ r, tab, dark, onEdit, onDecide, onRecord }) {
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.50)'
  const canEdit  = tab === 'mine' && (r.status === 'Submitted' || r.status === 'Rejected')

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '92px 1fr 150px 92px 96px 150px',
      gap: 10, alignItems: 'center', padding: '9px 20px',
      borderBottom: `0.5px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(7,39,45,0.07)'}`,
    }}>
      <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: textMut }}>{shortDate(r.date)}</div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textPrim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.payee || <span style={{ color: textMut }}>(no payee)</span>}
          {r.payor === 'Employee' && (
            <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: '#d97706', marginLeft: 8 }}>owed back</span>
          )}
        </div>
        <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMut, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(r.itemCode || '').replace(/^EX-\s*/, '')}{r.memo ? ` · ${r.memo}` : ''}
        </div>
        {r.status === 'Rejected' && r.workflowMemo && (
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#c0392b', marginTop: 3, whiteSpace: 'normal' }}>
            Sent back: {String(r.workflowMemo).split(' — ')[0]}
          </div>
        )}
        {r.core && r.core.status === 'failed' && (
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#c0392b', marginTop: 2 }}>Not in Core yet: {r.core.error}</div>
        )}
      </div>

      <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: textMut, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {r.projectName || (r.expenseType === 'Overhead' ? 'Overhead' : '—')}
      </div>

      <AmountCell r={r} dark={dark} />

      <div><StatusPill status={r.status} dark={dark} /></div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        {r.receipts.length > 0 && (
          <a href={r.receipts[0].url} target="_blank" rel="noreferrer"
            style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMut }} title="Open receipt">receipt</a>
        )}
        {canEdit && (
          <button onClick={() => onEdit(r)} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: textMut, background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
        )}
        {tab === 'review' && (<>
          <button onClick={() => onDecide(r, 'approve')} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1D9E75', background: 'none', border: 'none', cursor: 'pointer' }}>Approve</button>
          <button onClick={() => onDecide(r, 'reject')} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(192,57,43,0.8)', background: 'none', border: 'none', cursor: 'pointer' }}>Reject</button>
        </>)}
        {tab === 'record' && r.payor === 'Employee' && /^rec/.test(r.phaseId || '') && (
          <button onClick={() => onRecord(r, 'core')} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#07272D', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
            title="Write it to BQE Core as a pay-me-back expense on its phase">Send to Core</button>
        )}
        {/* Older pay-me-back expenses (no phase, filed before 2026-10-27) may already have been typed
            into Core by hand; sending them could make a second entry, so they keep the manual mark. */}
        {tab === 'record' && (r.payor !== 'Employee' || !/^rec/.test(r.phaseId || '')) && (
          <button onClick={() => onRecord(r)} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#07272D', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
            title="Company-paid: its Core entry comes from Bookkeeping's check for the card charge. Mark it once that check is in Core.">Mark in Core</button>
        )}
      </div>
    </div>
  )
}

/* ── Owed back: what the firm owes each person, paid monthly on payroll (Matt, 2026-10-27) ──
 * Only pay-me-back expenses already in Core appear. Once the payroll that carries them is run,
 * "Paid on payroll…" marks every one of that person's expenses paid in Core on that date. */
function OwedPanel({ dark }) {
  const [people, setPeople] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState('')
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.50)'
  const load = useCallback(() => { listOwed().then(d => setPeople(d.people || [])).catch(e => { setPeople([]); setErr(e.message) }) }, [])
  useEffect(() => { load() }, [load])
  async function paid(p) {
    const today = new Date().toISOString().slice(0, 10)
    const date = await promptDialog({
      title: `Paid ${p.name} back?`,
      message: `${money(p.total)} across ${p.expenses.length} expense${p.expenses.length === 1 ? '' : 's'}. Enter the payroll date it went out on (YYYY-MM-DD). Each one is marked paid in BQE Core.`,
      defaultValue: today, required: true, confirmLabel: 'Mark paid',
    })
    if (date === null) return
    setBusy(p.memberId || 'none')
    try {
      const r = await markExpensesPaid(p.expenses.map(e => e.id), date.trim())
      if (r.failed && r.failed.length) await alertDialog({ title: `${r.done.length} marked paid, ${r.failed.length} not`, message: r.failed.map(f => f.error).join(' · ') })
    } catch (e) { await alertDialog({ title: 'Not marked paid', message: e.message }) }
    setBusy(''); load()
  }
  return (
    <div style={{ overflowY: 'auto', flex: 1, padding: '6px 0' }}>
      {people === null && <div style={{ padding: 20, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut }}>Loading…</div>}
      {err && <div style={{ padding: 20, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b' }}>{err}</div>}
      {people && !err && people.length === 0 && <div style={{ padding: 24, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut }}>Nobody is owed anything. Approved pay-me-back expenses show here until the payroll that carries them is run.</div>}
      {(people || []).map(p => (
        <div key={p.memberId || 'none'} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 120px 150px', gap: 10, alignItems: 'center', padding: '10px 20px', borderBottom: `0.5px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(7,39,45,0.07)'}` }}>
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textPrim }}>{p.name}</div>
          <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: textMut }}>{p.expenses.length} expense{p.expenses.length === 1 ? '' : 's'}</div>
          <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 13, color: textPrim, textAlign: 'right' }}>{money(p.total)}</div>
          <div style={{ textAlign: 'right' }}>
            <button disabled={!!busy} onClick={() => paid(p)} style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#07272D', background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', fontWeight: 500 }}>
              {busy === (p.memberId || 'none') ? 'Marking…' : 'Paid on payroll…'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function ExpensesLauncher() {
  const dark = useDarkMode()
  const [open, setOpen]       = useState(false)
  const [tab, setTab]         = useState('mine')
  const [rows, setRows]       = useState([])
  const [can, setCan]         = useState({ review: false, record: false })
  const [counts, setCounts]   = useState({ review: null, record: null })
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [adding, setAdding]   = useState(false)
  const [editing, setEditing] = useState(null)
  const [projects, setProjects] = useState([])

  // The send-back Slack message links to ?expense=<id> (2026-10-28): open Mine with it in the form.
  const wantId = useRef(null)
  useEffect(() => {
    function onOpen(e) { if (e && e.detail && e.detail.expenseId) wantId.current = e.detail.expenseId; setTab('mine'); setOpen(true) }
    window.addEventListener('open-expenses-panel', onOpen)
    try {
      const q = new URLSearchParams(window.location.search).get('expense')
      if (q) { wantId.current = q; setTab('mine'); setOpen(true) }
    } catch { /* no deep link */ }
    return () => window.removeEventListener('open-expenses-panel', onOpen)
  }, [])
  useEffect(() => {
    if (!wantId.current || !rows.length) return
    const r = rows.find(x => x.id === wantId.current)
    if (r) { wantId.current = null; if (r.status === 'Submitted' || r.status === 'Rejected') { setEditing(r); setAdding(false) } }
  }, [rows])

  const load = useCallback(async (which) => {
    if (which === 'owed') { setRows([]); return }   // OwedPanel loads itself
    setLoading(true); setError('')
    try {
      const res = await listExpenseReports(which)
      setRows(res.reports || [])
      if (res.can) setCan(res.can)
    } catch (e) {
      setRows([])
      setError(e.message || 'Could not load expenses.')
    } finally { setLoading(false) }
  }, [])

  // Badge counts for the two queues, so an approver sees there is something waiting without
  // opening the tab. Failures are silent — a missing badge is not worth an error banner.
  const loadCounts = useCallback(async (perms) => {
    const next = { review: null, record: null }
    if (perms.review) { try { next.review = (await listExpenseReports('review')).reports.length } catch { /* silent */ } }
    if (perms.record) { try { next.record = (await listExpenseReports('record')).reports.length } catch { /* silent */ } }
    setCounts(next)
  }, [])

  useEffect(() => {
    if (!open) return
    load(tab)
    // getActiveProjects returns raw Airtable records; the picker wants plain objects.
    getActiveProjects()
      .then(recs => setProjects((recs || []).map(r => ({
        id: r.id,
        number: r.fields?.['Project Number'] || '',
        name: r.fields?.['Project Name'] || '',
      }))))
      .catch(() => setProjects([]))
  }, [open, tab, load])

  useEffect(() => { if (open) loadCounts(can) }, [open, can, loadCounts])

  async function refresh() { await load(tab); await loadCounts(can) }

  async function handleDecide(r, decision) {
    let memo = ''
    if (decision === 'reject') {
      memo = await promptDialog({
        title: 'Reject this expense?',
        message: `${r.payee || 'This expense'} · ${money(r.amount)}. Say what needs fixing — this goes back to whoever filed it.`,
        placeholder: 'Missing receipt / wrong project / not reimbursable',
        multiline: true, required: true,
      })
      if (memo === null) return
    } else {
      if (!(await confirmDialog({
        title: 'Approve this expense?',
        message: `${r.payee || 'This expense'} · ${money(r.amount)}${r.projectName ? ` · ${r.projectName}` : ''}. ${r.payor === 'Employee' ? 'It goes into BQE Core now, owed back to them on the next payroll.' : 'Company-paid: it reaches Core with the card charge in Bookkeeping.'}`,
        confirmLabel: 'Approve',
      }))) return
    }
    try {
      const res = await decideExpenseReport(r.id, decision, memo)
      if (res && res.core && res.core.ok === false) await alertDialog({ title: 'Approved, not in Core yet', message: res.core.error + " It waits in Accounting's To record list." })
      await refresh()
    }
    catch (e) { alertDialog({ title: 'Could not save that decision', message: e.message }) }
  }

  async function handleRecord(r, mode) {
    if (mode === 'core') {
      try {
        const res = await retryExpenseCore(r.id)
        if (res.core && res.core.ok) await refresh()
      } catch (e) { alertDialog({ title: 'Not sent to Core', message: e.message }); await refresh() }
      return
    }
    const note = await promptDialog({
      title: 'Recorded in Core?',
      message: `${r.payee || 'This expense'} · ${money(r.amount)}. Mark it as recorded once the entry exists in Core. Add the Core reference if you have one.`,
      placeholder: 'Core expense entry / check reference (optional)',
    })
    if (note === null) return
    try { await markExpenseRecorded(r.id, note); await refresh() }
    catch (e) { alertDialog({ title: 'Could not mark it recorded', message: e.message }) }
  }

  // Sum only what has a dollar value, and count what does not. A total that quietly swallows
  // unpriced mileage claims would understate what the firm owes and read as authoritative.
  const total   = useMemo(() => rows.reduce((s, r) => s + (r.amount == null ? 0 : Number(r.amount) || 0), 0), [rows])
  const unknown = useMemo(() => rows.filter(r => r.amount == null).length, [rows])

  if (!open) return null

  const bg      = dark ? '#0e2027' : '#fff'
  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)' : 'rgba(7,39,45,0.50)'
  const border   = dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.10)'

  const tabs = [
    { key: 'mine',   label: 'Mine',       show: true },
    { key: 'review', label: 'To approve', show: can.review, count: counts.review },
    { key: 'record', label: 'To record',  show: can.record, count: counts.record },
    { key: 'owed',   label: 'Owed back',  show: can.record },
  ].filter(t => t.show)

  const tabStyle = (t) => ({
    padding: '7px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12,
    fontWeight: tab === t ? 600 : 400, color: tab === t ? textPrim : textMut,
    background: 'none', border: 'none', cursor: 'pointer',
    borderBottom: tab === t ? `2px solid ${dark ? '#5bd6e8' : '#07272D'}` : '2px solid transparent',
  })

  return createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(7,39,45,0.32)', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px',
      }}
    >
      <div style={{
        width: 'min(1040px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        background: bg, borderRadius: 10, border: `0.5px solid ${border}`,
        boxShadow: '0 10px 40px rgba(7,39,45,0.22)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px 0' }}>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: textPrim }}>
            Expenses
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            {tab === 'mine' && !adding && !editing && (
              <button
                onClick={() => { setAdding(true); setEditing(null) }}
                style={{
                  fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500, color: '#fff',
                  background: '#07272D', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer',
                }}
              >
                New expense
              </button>
            )}
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMut, fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, padding: '6px 12px 0', borderBottom: `0.5px solid ${border}` }}>
          {tabs.map(t => (
            <button key={t.key} style={tabStyle(t.key)} onClick={() => { setTab(t.key); setAdding(false); setEditing(null) }}>
              {t.label}
              {t.count > 0 && (
                <span style={{
                  marginLeft: 6, fontFamily: '"DM Mono", monospace', fontSize: 10,
                  background: '#d97706', color: '#fff', borderRadius: 20, padding: '1px 6px',
                }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {(adding || editing) && (
          <ExpenseForm
            initial={editing}
            projects={projects}
            dark={dark}
            onSaved={async () => { setAdding(false); setEditing(null); await refresh() }}
            onCancel={() => { setAdding(false); setEditing(null) }}
          />
        )}

        {tab === 'owed' && <OwedPanel dark={dark} />}
        {/* List */}
        {tab !== 'owed' && <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 20, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut }}>Loading…</div>}
          {!loading && error && <div style={{ padding: 20, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b' }}>{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 24, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: textMut }}>
              {tab === 'mine' ? 'You have not filed any expenses yet.'
                : tab === 'review' ? 'Nothing waiting to be approved.'
                : 'Nothing approved and waiting to be recorded.'}
            </div>
          )}
          {!loading && !error && rows.map(r => (
            <Row key={r.id} r={r} tab={tab} dark={dark}
              onEdit={(row) => { setEditing(row); setAdding(false) }}
              onDecide={handleDecide}
              onRecord={handleRecord}
            />
          ))}
        </div>}

        {/* Footer total */}
        {rows.length > 0 && (
          <div style={{
            padding: '8px 20px', borderTop: `0.5px solid ${border}`,
            fontFamily: '"DM Mono", monospace', fontSize: 11, color: textMut,
            display: 'flex', justifyContent: 'space-between',
          }}>
            <span>
              {rows.length} expense{rows.length === 1 ? '' : 's'}
              {unknown > 0 && (
                <span style={{ color: '#d97706', marginLeft: 8 }}>
                  {unknown} claim{unknown === 1 ? '' : 's'} with no rate in Core
                </span>
              )}
            </span>
            <span>{money(total)}{unknown > 0 ? ' + mileage' : ''}</span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
